import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Checkbox, Form, Input, Label, MultiSelect, Select } from '@shared/components';
import { useFeeReferenceDataQuery } from '../hooks/useFeeReferenceDataQuery';
import { useFeesQuery } from '../hooks/useFeesQuery';
import { useProductFeeFormEffects } from '../hooks/useProductFeeFormEffects';
import { useProductFeeDerivation } from '../hooks/useProductFeeDerivation';
import { useFeePayerPairDerivation } from '../hooks/useFeePayerPairDerivation';
import { useVatDerivedCost } from '../hooks/useVatDerivedCost';
import { makeProductFeeSchema } from '../schemas/productFee.schema';
import type { ProductFeeFormInput, ProductFeeFormOutput } from '../schemas/productFee.schema';
import type { ProductFeePayload, ProductFeeRecord } from '../services/productFees';
import { buildProductFeeFormDefaults } from '../utils/productFeeFormDefaults';
import { withEditRowFee } from '../utils/feeCatalogue';
import { BandSubEditor } from './BandSubEditor';
import { PayerFields } from './ProductFeeForm/PayerFields';
import { VatCostFields } from './VatCostFields/VatCostFields';

interface Props {
    mode: 'create' | 'edit';
    row?: ProductFeeRecord;
    onSubmit: (payload: ProductFeePayload) => void;
    isLoading: boolean;
    error?: string | null;
    onCancel: () => void;
}

const DERIVED_HINT = "Set by the fee's transaction type";
const OVERRIDDEN_HINT = 'Product-specific override (read-only)';

const defaultValues: ProductFeeFormInput = {
    fee_id:               '',
    value_type_id:        '',
    scheduling_method_id: '',
    paid_by_id:           '',
    paid_to_id:           '',
    vat_category_id:      '',
    applies_to_ids:       [],
    application_scope:    'per_plan',
    is_absorbed:          false,
    cap_value_gross:      null,
    default_cost_net:     null,
    default_cost_gross:   null,
    default_percentage:   null,
    display_order:        undefined,
    bands:                undefined,
};

export const ProductFeeForm: React.FC<Props> = ({ mode, row, onSubmit, isLoading, error, onCancel }) => {
    const { data: refData, isLoading: isRefLoading } = useFeeReferenceDataQuery();
    const { data: feesData, isLoading: isFeesLoading } = useFeesQuery({ per_page: 100 });
    const fees = useMemo(
        () => withEditRowFee(feesData?.data ?? [], mode === 'edit' ? row : undefined),
        [feesData, mode, row],
    );
    const isPayerReferenceLoading = isRefLoading || isFeesLoading;

    const {
        control, handleSubmit, watch, reset, setValue, getValues, trigger,
        formState: { errors, isValid },
    } = useForm<ProductFeeFormInput, unknown, ProductFeeFormOutput>({
        mode: 'onChange',
        resolver: (values, ctx, options) => {
            const selectedValueTypeId = values.value_type_id;
            const valueType = refData?.fee_value_types.find((vt) => vt.id === selectedValueTypeId);
            const isPercentage = valueType?.is_percentage ?? false;
            const requiresLookup = valueType?.requires_lookup ?? false;
            const schema = makeProductFeeSchema(isPercentage, requiresLookup);
            return zodResolver(schema)(values, ctx, options);
        },
        defaultValues: mode === 'edit' && row ? buildProductFeeFormDefaults(row) : defaultValues,
    });

    const selectedValueTypeId = watch('value_type_id');
    const selectedValueType = refData?.fee_value_types.find((vt) => vt.id === selectedValueTypeId);
    const isPercentageType = selectedValueType?.is_percentage ?? false;
    const requiresLookup = selectedValueType?.requires_lookup ?? false;

    const applicationScope = watch('application_scope');
    const isPerServiceScope = applicationScope === 'per_service';

    const selectedVatRatePercent = refData?.vat_categories
        .find((v) => v.id === watch('vat_category_id'))?.rate_percent ?? '0';

    useProductFeeFormEffects({
        mode, refData, defaultValues, isPerServiceScope, reset, setValue, getValues, trigger,
    });

    useProductFeeDerivation({
        selectedValueType, isPercentageType, requiresLookup, setValue,
    });

    useVatDerivedCost({
        watch,
        setValue,
        netField:   'default_cost_net',
        grossField: 'default_cost_gross',
        fixedRatePercent:  selectedVatRatePercent,
        enabled:    !!selectedValueType && !isPercentageType,
    });

    const overridden = mode === 'edit' && row?.payer_pair_overridden === true;
    const { locked, unroutedCode } = useFeePayerPairDerivation({
        feeId: watch('fee_id'),
        fees,
        transactionTypes: refData?.transaction_types,
        overridden,
        setValue,
    });

    const handleFormSubmit = (data: ProductFeeFormOutput) => {
        onSubmit({
            fee_id:               data.fee_id,
            value_type_id:        data.value_type_id,
            scheduling_method_id: data.scheduling_method_id,
            paid_by_id:           data.paid_by_id,
            paid_to_id:           data.paid_to_id,
            vat_category_id:      data.vat_category_id,
            applies_to_ids:       data.applies_to_ids,
            application_scope:    data.application_scope,
            is_absorbed:          data.is_absorbed,
            cap_value_gross:      data.cap_value_gross ?? null,
            default_cost_net:     data.default_cost_net ?? null,
            default_percentage:   data.default_percentage ?? null,
            display_order:        data.display_order,
            bands:                requiresLookup ? data.bands : undefined,
        });
    };

    return (
        <Form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            {error && (
                <Alert variant="error">{error}</Alert>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <Label htmlFor="fee_id" required>Fee</Label>
                    <Controller
                        name="fee_id"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <Select
                                id="fee_id"
                                placeholder="Select fee"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={overridden}
                                error={!!fe}
                                hint={fe?.message}
                                isLoading={isFeesLoading}
                                options={fees.map((f) => ({ value: f.id, label: f.name }))}
                            />
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="value_type_id" required>Value Type</Label>
                    <Controller
                        name="value_type_id"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <Select
                                id="value_type_id"
                                placeholder="Select value type"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!fe}
                                hint={fe?.message}
                                isLoading={isRefLoading}
                                options={(refData?.fee_value_types ?? []).map((vt) => ({ value: vt.id, label: vt.name }))}
                            />
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="scheduling_method_id" required>Scheduling</Label>
                    <Controller
                        name="scheduling_method_id"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <Select
                                id="scheduling_method_id"
                                placeholder="Select scheduling"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!fe}
                                hint={fe?.message}
                                isLoading={isRefLoading}
                                options={(refData?.fee_scheduling_methods ?? []).map((m) => ({ value: m.id, label: m.name }))}
                            />
                        )}
                    />
                </div>

                <PayerFields
                    control={control}
                    targets={refData?.fee_targets ?? []}
                    isLoading={isRefLoading}
                    locked={locked}
                    lockedHint={overridden ? OVERRIDDEN_HINT : DERIVED_HINT}
                    unroutedCode={unroutedCode}
                />

                <div>
                    <Label htmlFor="vat_category_id" required>VAT Category</Label>
                    <Controller
                        name="vat_category_id"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <Select
                                id="vat_category_id"
                                placeholder="Select VAT category"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!fe}
                                hint={fe?.message}
                                isLoading={isRefLoading}
                                options={(refData?.vat_categories ?? []).map((v) => ({ value: v.id, label: v.name }))}
                            />
                        )}
                    />
                </div>

                <div>
                    <Controller
                        name="applies_to_ids"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <MultiSelect
                                label="Applies To"
                                required
                                options={refData?.quote_types ?? []}
                                value={field.value ?? []}
                                onChange={field.onChange}
                                loading={isRefLoading}
                                error={fe?.message}
                            />
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="application_scope" required>Scope</Label>
                    <Controller
                        name="application_scope"
                        control={control}
                        render={({ field, fieldState: { error: fe } }) => (
                            <Select
                                id="application_scope"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!fe}
                                hint={fe?.message}
                                options={[
                                    { value: 'per_plan', label: 'Per Plan' },
                                    { value: 'per_service', label: 'Per Service' },
                                ]}
                            />
                        )}
                    />
                </div>
            </div>

            <div>
                <Controller
                    name="is_absorbed"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            id="is_absorbed"
                            label={isPerServiceScope
                                ? 'Absorbed (not available for per-service scope)'
                                : 'Absorbed (excluded from plan total)'}
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={isPerServiceScope}
                        />
                    )}
                />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {isPercentageType ? (
                    <div>
                        <Label htmlFor="default_percentage" required>Default Percentage</Label>
                        <Controller
                            name="default_percentage"
                            control={control}
                            render={({ field, fieldState: { error: fe } }) => (
                                <Input
                                    id="default_percentage"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step={0.0001}
                                    value={(field.value as string | null) ?? ''}
                                    onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                                    onBlur={field.onBlur}
                                    error={!!fe}
                                    hint={fe?.message}
                                    placeholder="e.g. 5.0000"
                                />
                            )}
                        />
                    </div>
                ) : (
                    <VatCostFields<ProductFeeFormInput>
                        control={control}
                        hideCategory
                        netField="default_cost_net"
                        grossField="default_cost_gross"
                        categories={[]}
                        labels={{ net: 'Default Cost Net (£)', gross: 'Default Cost Gross (£)' }}
                    />
                )}

                {isPercentageType && (
                    <div>
                        <Label htmlFor="cap_value_gross">Cap Value Gross (£, optional)</Label>
                        <Controller
                            name="cap_value_gross"
                            control={control}
                            render={({ field, fieldState: { error: fe } }) => (
                                <Input
                                    id="cap_value_gross"
                                    type="number"
                                    min="0"
                                    step={0.0001}
                                    value={(field.value as string | null) ?? ''}
                                    onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                                    onBlur={field.onBlur}
                                    error={!!fe}
                                    hint={fe?.message}
                                    placeholder="0.00"
                                />
                            )}
                        />
                    </div>
                )}
            </div>

            {requiresLookup && (
                <BandSubEditor
                    control={control}
                    isPercentageType={isPercentageType}
                    vatRatePercent={selectedVatRatePercent}
                    setValue={setValue}
                    getValues={getValues}
                    watch={watch}
                />
            )}

            <div className="flex items-center gap-2 pt-2">
                <Button size="sm" disabled={!isValid || isLoading || isPayerReferenceLoading || unroutedCode !== null}>
                    {isLoading ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Fee'}
                </Button>
                <Button size="sm" type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                    Cancel
                </Button>
            </div>

            {errors.root && (
                <p className="text-sm text-mp-error">{errors.root.message}</p>
            )}
        </Form>
    );
};
